import { z } from "zod";

import type { ConfigurableTrtConfigFileFlags } from "./models.engine-config.types";
import {
  TRT_ATTENTION_BACKENDS,
  TRT_CAPACITY_SCHEDULER_POLICIES,
  TRT_CONTEXT_CHUNKING_POLICIES,
  TRT_GUIDED_DECODING_BACKENDS,
} from "./models.engine-config.types";

/**
 * The only config file a deployment may override. Patch manifests are
 * engine-config only, so they stay out of the public deployment API.
 */
export const TrtConfigFileSchema = z.object({
  name: z.literal("trt-config"),
  type: z.literal("yaml"),
  config: z.object({
    enable_chunked_prefill: z.boolean().optional(),
    max_input_len: z.number().optional(),
    trust_remote_code: z.boolean().optional(),
    disable_overlap_scheduler: z.boolean().optional(),
    stream_interval: z.number().optional(),
    enable_autotuner: z.boolean().optional(),
    guided_decoding_backend: z.enum(TRT_GUIDED_DECODING_BACKENDS).optional(),
    attn_backend: z.enum(TRT_ATTENTION_BACKENDS).optional(),
    scheduler_config: z
      .object({
        capacity_scheduler_policy: z
          .enum(TRT_CAPACITY_SCHEDULER_POLICIES)
          .optional(),
        context_chunking_policy: z
          .enum(TRT_CONTEXT_CHUNKING_POLICIES)
          .optional(),
      })
      .optional(),
    speculative_config: z
      .object({
        decoding_type: z.string().optional(),
        num_nextn_predict_layers: z.number().optional(),
      })
      .optional(),
    return_perf_metrics: z.boolean().optional(),
    perf_metrics_max_requests: z.number().optional(),
    kv_cache_config: z
      .object({
        enable_block_reuse: z.boolean().optional(),
        free_gpu_memory_fraction: z.number().optional(),
      })
      .optional(),
    cuda_graph_config: z
      .object({
        enable_padding: z.boolean().optional(),
        max_batch_size: z.number().optional(),
        batch_sizes: z.array(z.number()).optional(),
      })
      .optional(),
    moe_config: z
      .object({
        backend: z.string().optional(),
      })
      .optional(),
    torch_compile_config: z
      .object({
        enable_fullgraph: z.boolean().optional(),
        enable_piecewise_cuda_graph: z.boolean().optional(),
      })
      .optional(),
  }),
});

/**
 * A unified diff against the installed SGLang package, pinned to the exact
 * version and base-file hashes it was tested against. Generate it inside the
 * target image with `apps/cli/src/worker/lib/engines/sglang_patch.py manifest`.
 */
export const SglangPatchManifestSchema = z
  .object({
    formatVersion: z.literal(1),
    baseVersion: z.string().min(1).max(128),
    files: z
      .record(
        z.string().regex(/^(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]+\.py$/),
        z.string().regex(/^[a-f0-9]{64}$/),
      )
      .refine((files) => Object.keys(files).length > 0, {
        message: "Patch manifest must pin at least one file",
      }),
    diff: z.string().min(1).max(524_288),
  })
  .strict();

export type SglangPatchManifest = z.infer<typeof SglangPatchManifestSchema>;

export const ModelEngineConfigFileSchema = z.discriminatedUnion("name", [
  TrtConfigFileSchema,
  z
    .object({
      name: z.literal("sglang-patches"),
      type: z.literal("json"),
      config: SglangPatchManifestSchema,
    })
    .strict(),
]);

export type TrtConfigFile = z.infer<typeof TrtConfigFileSchema>;

export type ModelEngineConfigFile = z.infer<typeof ModelEngineConfigFileSchema>;

type ExpectedModelEngineConfigFile = {
  name: "trt-config";
  type: "yaml";
  config: ConfigurableTrtConfigFileFlags;
};

// Compile-time check that the runtime schema stays assignable to the legacy
// config-file type used by model engine config definitions.
const _configFileTypeCheck: ExpectedModelEngineConfigFile = null as never;
void _configFileTypeCheck;
